import { Colors, Helpers, Metrics } from '@origin-digital/style-guide';
import * as React from 'react';
import styled from 'styled-components';
import { SolarBillPeriod } from '../../../__generated__/globalTypes';
import analytics from '../../analytics';
import { Banner, FormErrorSummary, Layout } from '../../components';
import { CALCULATE_SOLAR_SAVINGS } from '../../queries';
import { CalculateSolarSaving, CalculateSolarSavingVariables } from '../../queries/__generated__/CalculateSolarSaving';
import { client } from '../../utils/apollo';
import { CalculatorForm, CalculatorResult } from './components';
import { CalculatorFormValues } from './components/CalculatorForm/CalculatorForm.types';
import { SolarCalculatorState } from './SolarCalculator.types';
import { CalculatorRecommendations } from './components/CalculatorResult/CalculatorResult.types';
import { CalculatorDisclaimer } from './components/CalculatorDisclaimer/CalculatorDisclaimer';

const Container = styled.div`
  background: ${Colors.GREY8};
`;

const Content = styled.div`
  padding-top: ${Metrics.Spacing.M_Y}px;
  padding-bottom: ${Metrics.Spacing.P_Y_XS}px;
  margin-left: -${Metrics.Spacing.M_X_SM}px;
  margin-right: -${Metrics.Spacing.M_X_SM}px;

  ${Helpers.media.LG`
    display: flex;
    justify-content: center;
    align-items: start;
    margin-left: auto;
    margin-right: auto;
  `};
`;

class SolarCalculator extends React.Component<{}, SolarCalculatorState> {
  resultRef: React.RefObject<CalculatorResult>;
  disclaimerRef: React.RefObject<CalculatorDisclaimer>;

  constructor(props: {}) {
    super(props);

    this.onGraphError = this.onGraphError.bind(this);

    this.state = {
      graphError: false,
      graphMessage: 'Sorry, something went wrong. Please try again.',
      loading: false,
      recommendations: false,
      loaded: false,
      values: false,
      calculationCount: 0
    };

    this.resultRef = React.createRef();
    this.disclaimerRef = React.createRef();
  }

  componentDidMount() {
    analytics.pageLoad('solar-estimate-savings');
  }

  onGraphError(value: boolean, errorMessage: string | undefined): void {
    this.setState({
      graphError: value,
      graphMessage: errorMessage || 'Sorry, something went wrong. Please try again.'
    });

    if (value) analytics.error(this.state.graphMessage!);
  }

  async submit(values: CalculatorFormValues) {
    this.setState({ loading: true, values });
    const variables: CalculateSolarSavingVariables = {
      options: {
        address: { postcode: values.postcode },
        billCostForPeriod: parseFloat(values.averageBill),
        billPeriod: SolarBillPeriod.QUARTERLY,
        usagePercentDuringDay: parseFloat(values.usagePercentDuringDay)
      }
    };

    let recommendations;
    try {
      recommendations = await client.query<CalculateSolarSaving, CalculateSolarSavingVariables>({
        query: CALCULATE_SOLAR_SAVINGS,
        variables,
        fetchPolicy: 'network-only'
      });

      // Wait for view to render, then scroll down on mobile
      setTimeout(() => {
        this.resultRef.current!.scrollTo();
      }, 100);
    } catch (e) {
      this.setState({
        loading: false,
        graphError: true,
        graphMessage: e.message
      });
      return;
    }

    analytics.submitSuccessful({
      currentAction: {
        postcode: values.postcode,
        quarterlyBillSize: values.averageBill,
        typicalEnergyUsageTime: values.usagePercentDuringDay
      }
    });

    this.setState({
      loading: false,
      loaded: true,
      recommendations: recommendations.data,
      calculationCount: this.state.calculationCount + 1
    });
  }

  onShowDisclaimer() {
    const disclaimerRef = this.disclaimerRef.current;
    if (disclaimerRef) {
      disclaimerRef.scrollTo();
    }
  }

  render(): JSX.Element {
    const { loading, graphError, graphMessage, recommendations, loaded, values, calculationCount } = this.state;

    let r: CalculatorRecommendations | null = null;
    if (recommendations) r = recommendations.calculateSolarRecommendations!;

    return (
      <Container data-id="solar-calculator">
        <Banner
          title={'What savings could you make with solar?'}
          subtitle={
            'We can estimate your purchase cost, your electricity bill savings, and how long till your system is paying for itself.'
          }
        />
        <Layout min={['LG', 'XL']} max={[]} paddingX={4}>
          <FormErrorSummary graphError={graphError} graphMessage={graphMessage} marginBottom={0} />
          <Content>
            <CalculatorForm
              onGraphError={this.onGraphError}
              graphError={graphError}
              graphMessage={graphMessage}
              onSubmit={this.submit.bind(this)}
              loading={loading}
            />
            <CalculatorResult
              loading={loading}
              loaded={loaded}
              recommendations={r || undefined}
              values={values}
              onShowDisclaimer={() => this.onShowDisclaimer()}
              calculationCount={calculationCount}
              ref={this.resultRef}
            />
          </Content>
        </Layout>
        <CalculatorDisclaimer ref={this.disclaimerRef} />
      </Container>
    );
  }
}

export { SolarCalculator as default };
